import React, { useState, useEffect } from "react";

const UpdateInternAmount = () => {
  const [config, setConfig] = useState(null);
  const [internId, setInternId] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [intern, setIntern] = useState(null);

  // Load config
  useEffect(() => {
    fetch("/config.json")
      .then((r) => r.json())
      .then(setConfig)
      .catch(() => setFetchError("Failed to load config.json"));
  }, []);

  // Fetch details by intern_id
  const handleFetch = async (e) => {
    e.preventDefault();
    setFetchError("");
    setSuccessMsg("");
    setUpdateError("");
    setIntern(null);

    if (!internId.trim()) {
      setFetchError("Please enter an Intern ID.");
      return;
    }
    if (!config) return;

    try {
      setLoading(true);
      const res = await fetch(
        `${config.BASE_URL}${config.FETCH_INTERN_BY_ID}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intern_id: internId.trim() }),
        }
      );

      const data = await res.json();
      if (!data.success || !data.intern) {
        setFetchError(data.message || "No intern found for this ID.");
        setIntern(null);
      } else {
        const internWithId = { ...data.intern, intern_id: data.intern.application_id };
        setIntern(internWithId);
      }
    } catch (err) {
      setFetchError("Failed to fetch intern details.");
    } finally {
      setLoading(false);
    }
  };

  // Update all editable details (except amount)
  const handleUpdate = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setUpdateError("");

    if (!intern) {
      setUpdateError("Fetch an intern first.");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        `${config.BASE_URL}${config.UPDATE_INTERN_AMOUNT}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            intern_id: intern.intern_id,
            name: intern.name,
            email: intern.email,
            phone: intern.phone,
            college: intern.college,
            cgpa: intern.cgpa,
          }),
        }
      );

      const data = await res.json();
      if (data.success) {
        setSuccessMsg("Details updated successfully!");
      } else {
        setUpdateError(data.message || "Failed to update details.");
      }
    } catch (err) {
      setUpdateError("Error updating details.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setIntern((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  return (
    <div
      className="update-intern-amount"
      style={{
        maxWidth: 720,
        margin: "20px auto",
        padding: 16,
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      }}
    >
      <h2 style={{ marginBottom: 16 }}>Update Intern Details</h2>

      {/* Fetch form */}
      <form
        onSubmit={handleFetch}
        style={{ display: "flex", gap: 8, marginBottom: 16 }}
      >
        <input
          type="text"
          placeholder="Enter Intern ID"
          value={internId}
          onChange={(e) => setInternId(e.target.value)}
          style={{ flex: 1, padding: 10, border: "1px solid #ddd", borderRadius: 6 }}
        />
        <button
          type="submit"
          disabled={loading || !config}
          style={{
            padding: "10px 16px",
            border: "none",
            background: "#0a7cff",
            color: "#fff",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {loading ? "Fetching..." : "Fetch Details"}
        </button>
      </form>

      {fetchError && <div style={{ color: "#b00020", marginBottom: 12 }}>{fetchError}</div>}

      {/* Intern details */}
      {intern && (
        <form
          onSubmit={handleUpdate}
          style={{
            border: "1px solid #eee",
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            display: "grid",
            gridTemplateColumns: "160px 1fr",
            rowGap: 8,
            gap: 8,
          }}
        >
          {/* Non-editable fields */}
          <div style={{ color: "#666" }}>Application ID</div>
          <div>{intern.intern_id}</div>

          <div style={{ color: "#666" }}>Domain</div>
          <div>{intern.domain}</div>

          <div style={{ color: "#666" }}>Status</div>
          <div>{intern.status === 1 ? "Approved" : intern.status === 0 ? "Pending" : intern.status}</div>

          {/* Editable fields */}
          <div style={{ color: "#666" }}>Name</div>
          <input type="text" value={intern.name || ""} onChange={(e) => handleChange("name", e.target.value)} />

          <div style={{ color: "#666" }}>Email</div>
          <input type="email" value={intern.email || ""} onChange={(e) => handleChange("email", e.target.value)} />

          <div style={{ color: "#666" }}>Phone</div>
          <input type="text" value={intern.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} />

          <div style={{ color: "#666" }}>College</div>
          <input type="text" value={intern.college || ""} onChange={(e) => handleChange("college", e.target.value)} />

          <div style={{ color: "#666" }}>CGPA</div>
          <input type="number" step="0.01" value={intern.cgpa || ""} onChange={(e) => handleChange("cgpa", e.target.value)} />

          {/* Non-editable Amount */}
          <div style={{ color: "#666" }}>Amount</div>
          <div>{intern.amount ?? "-"}</div>

          {/* Update button */}
          <div></div>
          <button
            type="submit"
            disabled={loading || !config}
            style={{
              padding: "10px 16px",
              border: "none",
              background: "#27ae60",
              color: "#fff",
              borderRadius: 6,
              cursor: "pointer",
              marginTop: 8,
            }}
          >
            {loading ? "Saving..." : "Update Details"}
          </button>
        </form>
      )}

      {updateError && <div style={{ color: "#b00020", marginTop: 12 }}>{updateError}</div>}
      {successMsg && <div style={{ color: "#2e7d32", marginTop: 12 }}>{successMsg}</div>}
    </div>
  );
};

export default UpdateInternAmount;
